<script lang="ts">
    import AddProductToWishlistButton from "@apps/iloom_customization/components/AddProductToWishlistButton.svelte";
    import CloseIcon from "@apps/iloom_customization/components/icons/CloseIcon.svelte";
    import { ReactionPayload } from "@apps/iloom_customization/design_feed/api_services/DesignFeedApiService";
    import DesignFeedArrowRightIcon from "@apps/iloom_customization/design_feed/components/icons/DesignFeedArrowRightIcon.svelte";
    import DesignFeedPinIcon from "@apps/iloom_customization/design_feed/components/icons/DesignFeedPinIcon.svelte";
    import { MediaListQuery } from "@apps/iloom_customization/design_feed/queries/MediaListQuery";
    import { DesignFeedService } from "@apps/iloom_customization/design_feed/services/DesignFeedService";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import { ProductService } from "@apps/iloom_customization/modules/products/services/ProductService";
    import { designFeedModalToken } from "@apps/iloom_customization/tokens";
    import { WishlistService } from "@apps/iloom_customization/wishlist/services/WishlistService";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import cn from "classnames";
    import { lastValueFrom } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import ManagedModalWithDialog from "@components/Modal/ManagedModalWithDialog.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import ModalManagerProvider from "@components/Modal/ModalManager/ModalManagerProvider.svelte";
    import Money from "@components/Money.svelte";

    import AddMediaToWishlistButton from "./AddMediaToWishlistButton.svelte";
    import DesignFeedModalAddToCart from "./DesignFeedModalAddToCartButton.svelte";
    import DesignFeedModalReactionButton from "./DesignFeedModalReactionButton.svelte";

    export let cartQuery: CartQuery;
    export let cartService: CartService;
    export let designFeedService: DesignFeedService;
    export let mediaListQuery: MediaListQuery;
    export let modalManager: ModalManager;
    export let productQuery: ProductQuery;
    export let productService: ProductService;
    export let wishlistService: WishlistService;
    export let wishlistsService: WishlistsService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;

    const urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;

    modalManager.addModal(designFeedModalToken);

    const params$ = modalManager.selectParams(designFeedModalToken);

    const mediaList$ = mediaListQuery.mediaList$;

    const gqlProducts$ = productQuery.all$;

    let selectedProductId: number | null = null;

    const setSelectedProductId = (id: number | null) => {
        selectedProductId = id;
    };

    const handleAddFaveMedia = (mediaId: string) => {
        lastValueFrom(designFeedService.addFave(mediaId));
    };

    const handleRemoveFaveMedia = (mediaId: string) => {
        lastValueFrom(designFeedService.removeFave(mediaId));
    };

    const handleAddReaction = ({ productId, variantId }: ReactionPayload) => {
        lastValueFrom(designFeedService.addReaction({ productId, variantId }));
    };

    $: curMedia = $mediaList$.find((el) => el.mediaId === $params$?.id)!;

    $: media = {
        ...curMedia,
        products: curMedia?.products?.map((p) => {
            return {
                ...p,
                product_wish_button: $gqlProducts$.find(
                    (gqlProduct) => gqlProduct.id === p.productId
                )?.product_wish_button,
                tags: $gqlProducts$
                    .find((gqlProduct) => gqlProduct.id === p.productId)
                    ?.tags.filter((tag) =>
                        tag.toLocaleLowerCase().includes("series")
                    )
                    .map((tag) => tag.replace("series: ", "")),
                compareAtPrice: $gqlProducts$
                    .find((gqlProduct) => gqlProduct.id === p.productId)
                    ?.variants.find((variant) => variant.id === p.variantId)
                    ?.compareAtPrice?.amount,
            };
        }),
    };

    $: curMedia?.products.forEach((p) =>
        productService.addProductForFetching(p.productId)
    );

    onMount(() => {
        lastValueFrom(wishlistService.getWishlists());
    });
</script>

<ModalManagerProvider {modalManager}>
    <ManagedModalWithDialog
        modalToken={designFeedModalToken}
        modalClass="gw-df-modal"
        dialogClass="gw-df-modal-dialog"
        closeIcon={CloseIcon}
        disablePortal
    >
        <div
            class="gw-df-modal-image"
            style={`background-image: url(${media?.src})`}
        >
            <div class="gw-df-modal-image-pins-field">
                <img src={media?.src} alt={media?.mediaId} />
                {#each media?.products as pin}
                    <div
                        on:mouseenter={() =>
                            setSelectedProductId(pin.productId)}
                        on:mouseleave={() => setSelectedProductId(null)}
                        class={"gw-df-modal-image-pin-wrapper"}
                        style={`--gw-df-modal-pin-top: ${pin.yCoordinate}; --gw-df-modal-pin-left: ${pin.xCoordinate}`}
                    >
                        <span
                            class={cn("gw-df-modal-image-pin", {
                                hovered: pin.productId === selectedProductId,
                            })}
                        >
                            <DesignFeedPinIcon />
                        </span>
                        {#if pin.productId === selectedProductId}
                            <div
                                class="gw-df-modal-image-pin-product"
                                style={`--gw-df-modal-pin-product-left: ${
                                    pin.xCoordinate > 0.5 ? "unset" : "0"
                                }; --gw-df-modal-pin-product-right: ${
                                    pin.xCoordinate > 0.5 ? "0" : "unset"
                                }`}
                            >
                                {#if pin?.tags?.length}
                                    <div
                                        class="gw-df-modal-product__tags gw-df-modal-image-pin-product__tags"
                                    >
                                        {#each pin.tags as tag}
                                            <span>{tag}</span>
                                        {/each}
                                    </div>
                                {/if}
                                <div
                                    class="gw-df-modal-product__title gw-df-modal-image-pin-product__title"
                                >
                                    {pin.title}
                                </div>
                                {#if pin.product_wish_button}
                                    <div
                                        class="gw-df-modal-product__not-for-sale-tag"
                                    >
                                        我想買
                                    </div>
                                {:else}
                                    <div
                                        class="gw-df-modal-image-pin-product__price-wrapper"
                                    >
                                        {#if pin.price}
                                            <span
                                                class="gw-df-modal-product__price"
                                            >
                                                <Money
                                                    with_currency
                                                    amount={pin.price}
                                                />
                                            </span>
                                        {/if}
                                        {#if pin.compareAtPrice}
                                            <span
                                                class="gw-df-modal-product__price-compare"
                                            >
                                                <Money
                                                    with_currency
                                                    amount={pin.compareAtPrice}
                                                />
                                            </span>
                                        {/if}
                                    </div>
                                {/if}

                                <a
                                    href={urlsBuilder.createProductUrl(
                                        pin.handle
                                    )}
                                    class="gw-df-modal-product__link gw-df-modal-image-pin-product__link"
                                    aria-label={pin.title || "Browse product"}
                                >
                                    <DesignFeedArrowRightIcon />
                                </a>
                            </div>
                        {/if}
                    </div>
                {/each}
                <AddMediaToWishlistButton
                    class="gw-df-modal-wishlist-btn"
                    inWishlist={!!media.didViewerFave}
                    handleDelete={() => handleRemoveFaveMedia(media.mediaId)}
                    handleAdd={() => handleAddFaveMedia(media.mediaId)}
                />
            </div>
        </div>

        <div class="gw-df-modal-info">
            <div class="gw-df-modal-category">
                <div class="gw-df-modal-category__title">#TAG</div>
                <div class="gw-df-modal-category__tags">
                    {#each media?.tags as tag}
                        <span class="gw-df-modal-category__tag">
                            {tag?.replace("series: ", "")}
                        </span>
                    {/each}
                </div>
            </div>
            <div class="gw-df-modal-category">
                <div class="gw-df-modal-category__title">SPACE</div>
                <div class="gw-df-modal-category__tags">
                    {#each media?.collections as collection}
                        <span class="gw-df-modal-category__tag">
                            {collection}
                        </span>
                    {/each}
                </div>
            </div>

            <div class="gw-df-modal-divider" />

            <div>
                {#each media.products as product}
                    <div
                        class={"gw-df-modal-product-item"}
                        on:mouseenter={() =>
                            setSelectedProductId(product.productId)}
                        on:mouseleave={() => setSelectedProductId(null)}
                    >
                        <a
                            href={urlsBuilder.createProductUrl(product.handle)}
                            class="gw-df-modal-product__link"
                            aria-label={product.title || "Browse product"}
                        >
                            <div
                                class="gw-df-modal-product-item__image-wrapper"
                            >
                                <img
                                    src={product.imageUrl}
                                    alt={product.title}
                                />
                            </div>
                        </a>
                        <div class="gw-df-modal-product-item__info">
                            {#if product?.tags?.length}
                                <div
                                    class="gw-df-modal-product__tags gw-df-modal-product-item__tags"
                                >
                                    {#each product.tags as tag}
                                        <span>{tag}</span>
                                    {/each}
                                </div>
                            {/if}
                            <div
                                class="gw-df-modal-product__title gw-df-modal-product-item__title"
                            >
                                <a
                                    href={urlsBuilder.createProductUrl(
                                        product.handle
                                    )}
                                    class="gw-df-modal-product__link"
                                    aria-label={product.title ||
                                        "Browse product"}
                                >
                                    {product.title}
                                </a>
                            </div>

                            {#if product.product_wish_button}
                                <div
                                    class="gw-df-modal-product__not-for-sale-tag"
                                >
                                    不出售
                                </div>
                            {:else}
                                <div
                                    class="gw-df-modal-product-item__price-wrapper"
                                >
                                    {#if product.price}
                                        <span
                                            class="gw-df-modal-product__price"
                                        >
                                            <Money
                                                with_currency
                                                amount={product.price}
                                            />
                                        </span>
                                    {/if}
                                    {#if product.compareAtPrice}
                                        <span
                                            class="gw-df-modal-product__price-compare"
                                        >
                                            <Money
                                                with_currency
                                                amount={product.compareAtPrice}
                                            />
                                        </span>
                                    {/if}
                                </div>
                            {/if}

                            {#if product.product_wish_button}
                                <div
                                    class="gw-df-modal-product-item__buttons gw-df-modal-product-item__buttons-end"
                                >
                                    <DesignFeedModalReactionButton
                                        didViewerReact={!!product.didViewerReact}
                                        disabled={!!product.didViewerReact}
                                        on:click={() =>
                                            handleAddReaction({
                                                productId: product.productId,
                                                variantId: product.variantId,
                                            })}
                                    />
                                </div>
                            {:else}
                                <div class="gw-df-modal-product-item__buttons">
                                    <AddProductToWishlistButton
                                        variantId={product.variantId}
                                        productId={product.productId}
                                        {wishlistsService}
                                        {wishlistProductStatusesQuery}
                                    />
                                    <DesignFeedModalAddToCart
                                        {cartQuery}
                                        {cartService}
                                        {product}
                                    />
                                </div>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        </div>
    </ManagedModalWithDialog>
</ModalManagerProvider>

<style lang="postcss">
    :global(.gw-modal.gw-df-modal) {
        color: #3c3c3c;
    }
    :global(.gw-df-modal .gw-modal-dialog.gw-df-modal-dialog) {
        max-width: min(85vw, 1200px);
        width: 100%;
        max-height: min(calc(100vh - 120px), 960px);
        height: 100%;
        padding: 0;
        border-radius: 10px 10px 0 0;

        @supports (height: 100dvh) {
            max-height: min(calc(100dvh - 120px), 960px);
        }
    }
    :global(.gw-df-modal .gw-modal-body) {
        --gw-modal-padding: 80px 40px;
        height: 100%;
        display: flex;
        gap: 40px;
    }
    :global(
            .gw-df-modal
                .gw-modal-dialog.gw-df-modal-dialog
                .gw-modal-header__close_btn--absolute
        ) {
        right: 24px;
        top: 24px;
    }

    .gw-df-modal {
        &-image {
            position: relative;
            width: 60%;
            display: flex;
            justify-content: center;
            align-items: center;
            background-size: cover;

            &::before {
                content: "";
                position: absolute;
                width: 100%;
                height: 100%;
                backdrop-filter: blur(5px);
                background-color: #0000004d;
            }
            &-pins-field {
                max-width: 100%;
                max-height: 100%;
                position: relative;
                display: flex;
                img {
                    max-width: 100%;
                    max-height: 100%;
                }
            }
            &-pin {
                --gw-pin-scale: 1;
                display: flex;
                transform: scale(var(--gw-pin-scale));
                transition: transform 0.3s ease;
                &.hovered {
                    --gw-pin-scale: 1.2;
                }
                &-wrapper {
                    position: absolute;
                    top: calc(var(--gw-df-modal-pin-top) * 100%);
                    left: calc(var(--gw-df-modal-pin-left) * 100%);
                    transform: translate(-50%, -50%);
                }
                &-product {
                    position: absolute;
                    z-index: 99;
                    bottom: 100%;
                    left: var(--gw-df-modal-pin-product-left);
                    right: var(--gw-df-modal-pin-product-right);
                    width: 275px;
                    background-color: #fff;
                    padding: 16px 43px 16px 16px;
                    &__tags {
                        font-size: 10px;
                        margin-bottom: 6px;
                    }
                    &__title {
                        font-size: 13px;
                        margin-bottom: 12px;
                    }
                    &__price-wrapper {
                        font-weight: 400;
                        font-size: 14px;
                    }
                    &__link {
                        position: absolute;
                        top: 50%;
                        right: 0;
                        transform: translateY(-50%);
                        padding: 16px;
                    }
                }
            }
        }
        &-info {
            padding: 20px 0;
            width: 40%;
            overflow-y: auto;
            &::-webkit-scrollbar {
                display: none;
            }
        }
        &-category {
            display: flex;
            &:first-of-type {
                margin-bottom: 20px;
            }
            &__title {
                min-width: 60px;
                font-weight: 500;
                font-size: 14px;
                line-height: 35px;
            }
            &__tags {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
            }
            &__tag {
                padding: 10px 12px 10px 12px;
                font-weight: 400;
                font-size: 12px;
                line-height: 1.25;
                text-transform: uppercase;
                background-color: #f0f0f0;
                border-radius: 30px;
            }
        }
        &-product-item {
            display: flex;
            gap: 20px;
            color: #3c3c3c;

            &:not(:last-child) {
                margin-bottom: 20px;
            }
            &__image-wrapper {
                width: 140px;
                height: 140px;
                display: flex;
                justify-content: center;
                align-items: center;
                flex-shrink: 0;
                background-color: #f2efea;
                img {
                    max-width: 80%;
                    max-height: 80%;
                }
            }
            &__info {
                flex: 1;
                display: flex;
                flex-direction: column;
            }

            &__tags {
                font-size: 14px;
                margin-bottom: 8px;
            }

            &__title {
                font-size: 16px;
                margin-bottom: 16px;
            }
            &__price-wrapper {
                display: flex;
                gap: 16px;
                font-size: 16px;
                font-weight: 400;
                margin-top: 16px;
            }
            &__buttons {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                gap: 10px;
                margin-top: 8px;
                &-end {
                    flex: 1;
                    align-items: flex-end;
                }
            }
        }
        &-divider {
            display: block;
            border-bottom: 1px dashed #bebebe;
            margin-top: 26px;
            margin-bottom: 40px;
        }
        &-product {
            &__title {
                font-family: "Noto Sans TC";
                font-weight: 500;
                line-height: 1.5;
            }
            &__tags {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
                font-family: "Montserrat";
                font-weight: 400;
                line-height: 1.25;
            }
            &__not-for-sale-tag {
                width: 51px;
                height: 22px;
                color: #9a9a9a;
                background-color: #f0f0f0;
                border-radius: 2px;
                font-family: "Avenir";
                font-size: 10px;
                font-weight: 900;
                line-height: 22px;
                text-align: center;
            }
            &__price {
                color: #3c3c3c;
            }
            &__price-compare {
                color: #666666;
                text-decoration: line-through;
            }
            &__link {
                text-decoration: none;
                color: inherit;
                font-size: inherit;
            }
        }
    }
    @media all and (max-width: 600px) {
        :global(.gw-modal.gw-df-modal .gw-modal__scrollable.gw-scrollable) {
            align-items: flex-start;
        }
        :global(.gw-df-modal .gw-modal-dialog.gw-df-modal-dialog) {
            max-width: 100vw;
            max-height: none;
            height: auto;
            top: 80px;
        }
        :global(.gw-df-modal .gw-modal-body) {
            --gw-modal-padding: 64px 20px;
            padding: 40px 0;
            flex-direction: column;
            gap: 0;
        }
        :global(.gw-btn.gw-media-wishlist-btn.gw-df-modal-wishlist-btn) {
            right: 15px;
            bottom: 15px;
        }
        .gw-df-modal {
            &-image,
            &-info {
                width: 100%;
            }
            &-image {
                &-pin {
                    --gw-pin-scale: 0.5;
                    &.hovered {
                        --gw-pin-scale: 0.7;
                    }
                    &-wrapper {
                        display: none;
                    }
                }
            }
            &-category {
                &__title {
                    font-size: 12px;
                    line-height: 23px;
                }
                &__tag {
                    padding: 4px 12px 4px 12px;
                }
            }
            &-product-item {
                &__image-wrapper {
                    width: 120px;
                    height: 120px;
                }
                &__tags {
                    margin-bottom: 4px;
                }
                &__title {
                    font-size: 14px;
                    margin-bottom: 8px;
                }
                &__price-wrapper {
                    font-size: 14px;
                    margin-top: 8px;
                }
            }
        }
    }
</style>
